import { useEffect, useMemo, useRef } from "react";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActiveCustomerGroups } from "@/features/customerGroups/hooks/useCustomerGroups";
import { useCounties, usePaymentTerms } from "@/shared/hooks/useMetaData";
import { PhoneNumberInput } from "@/shared/components/PhoneNumberInput";
import { useFormFieldErrors } from "@/shared/contexts/field-error-context";
import { FormErrorSummary } from "@/shared/components/forms/FormErrorSummary";

interface CustomerFormData {
  customerType: "Individual" | "Organization";
  customerName: string;
  idNumber: string;
  mobileNo1: string;
  mobileNo2: string;
  email: string;
  county: string;
  town: string;
  road: string;
  physicalLocationName: string;
  gpsLocation: string;
  customerGroupId: string;
  creditLimit: number;
  paymentTerms: string;
}

interface CustomerFormProps {
  formId: string;
  data: CustomerFormData;
  onChange: (field: keyof CustomerFormData, value: string | number) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  submitButtonText: string;
}

const FieldErrorBlock = ({ messages }: { messages: string[] | undefined }) =>
  messages && messages.length > 0 ? (
    <div role="alert" className="text-red-500 text-xs mt-1">
      {messages.join(", ")}
    </div>
  ) : null;

export const CustomerForm = ({
  formId,
  data,
  onChange,
  onSubmit,
  isLoading,
  submitButtonText,
}: CustomerFormProps) => {
  const { data: customerGroups, isLoading: groupsLoading } =
    useActiveCustomerGroups();
  const { data: paymentTerms, isLoading: termsLoading } = usePaymentTerms();
  const { data: counties, isLoading: countiesLoading } = useCounties();

  const fieldErrorsList = useFormFieldErrors(formId);
  const fieldErrors = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const fe of fieldErrorsList) {
      (out[fe.field] ??= []).push(fe.userMessage);
    }
    return out;
  }, [fieldErrorsList]);

  const errorSummaryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (fieldErrorsList.length > 0) {
      errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [fieldErrorsList.length]);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div ref={errorSummaryRef}>
        <FormErrorSummary formId={formId} />
      </div>

      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-bold mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
          <Field>
            <FieldLabel>Customer Type</FieldLabel>
            <Select
              value={data.customerType}
              onValueChange={(value: "Individual" | "Organization") => onChange("customerType", value)}
              disabled={isLoading}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Individual">Individual</SelectItem>
                <SelectItem value="Organization">Organization</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="customerName">
              Customer Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="customerName"
              value={data.customerName}
              onChange={(e) => onChange("customerName", e.target.value)}
              required
              disabled={isLoading}
              maxLength={200}
              aria-invalid={!!fieldErrors.customerName}
            />
            <FieldErrorBlock messages={fieldErrors.customerName} />
          </Field>

          <Field>
            <FieldLabel htmlFor="idNumber">
              {data.customerType === "Organization" ? "Registration Number" : "ID Number"}
            </FieldLabel>
            <Input
              id="idNumber"
              value={data.idNumber}
              onChange={(e) => onChange("idNumber", e.target.value)}
              disabled={isLoading}
              placeholder={data.customerType === "Organization" ? "Enter registration number (optional)" : "Enter ID number (optional)"}
              maxLength={50}
              aria-invalid={!!fieldErrors.idNumber}
            />
            <FieldErrorBlock messages={fieldErrors.idNumber} />
          </Field>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-bold mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 gap-4">
          <PhoneNumberInput
            value={data.mobileNo1}
            onChange={(value) => onChange("mobileNo1", value)}
            label="Mobile Number 1"
            required
            disabled={isLoading}
            errors={fieldErrors.mobileNo1}
            placeholder="Enter primary mobile number"
          />

          <PhoneNumberInput
            value={data.mobileNo2}
            onChange={(value) => onChange("mobileNo2", value)}
            label="Mobile Number 2"
            disabled={isLoading}
            errors={fieldErrors.mobileNo2}
            placeholder="Enter secondary mobile number (optional)"
          />

          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => onChange("email", e.target.value)}
              disabled={isLoading}
              maxLength={255}
              aria-invalid={!!fieldErrors.email}
              placeholder="Optional"
            />
            <FieldErrorBlock messages={fieldErrors.email} />
          </Field>
        </div>
      </div>

      {/* Address Information */}
      <div>
        <h3 className="text-lg font-bold mb-4">Address Information</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
          <Field>
            <FieldLabel htmlFor="county">County <span className="text-red-500">*</span></FieldLabel>
            {countiesLoading ? (
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
            ) : (
              <Select
                value={data.county}
                onValueChange={(value) => onChange("county", value)}
                disabled={isLoading || countiesLoading}
              >
                <SelectTrigger id="county" className="w-full" aria-invalid={!!fieldErrors.county}>
                  <SelectValue placeholder="Select County" />
                </SelectTrigger>
                <SelectContent>
                  {counties?.map((county) => (
                    <SelectItem key={county.code} value={county.name}>
                      {county.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <FieldErrorBlock messages={fieldErrors.county} />
          </Field>

          <Field>
            <FieldLabel htmlFor="town">
              Town <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="town"
              value={data.town}
              onChange={(e) => onChange("town", e.target.value)}
              required
              disabled={isLoading}
              maxLength={100}
              aria-invalid={!!fieldErrors.town}
            />
            <FieldErrorBlock messages={fieldErrors.town} />
          </Field>

          <Field>
            <FieldLabel htmlFor="road">
              Road <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="road"
              value={data.road}
              onChange={(e) => onChange("road", e.target.value)}
              required
              disabled={isLoading}
              maxLength={200}
              aria-invalid={!!fieldErrors.road}
            />
            <FieldErrorBlock messages={fieldErrors.road} />
          </Field>

          <Field>
            <FieldLabel htmlFor="physicalLocationName">
              Physical Location Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="physicalLocationName"
              value={data.physicalLocationName}
              onChange={(e) => onChange("physicalLocationName", e.target.value)}
              disabled={isLoading}
              required
              maxLength={200}
              aria-invalid={!!fieldErrors.physicalLocationName}
            />
            <FieldErrorBlock messages={fieldErrors.physicalLocationName} />
          </Field>
        </div>

        <Field className="mt-4">
          <FieldLabel htmlFor="gpsLocation">GPS Location</FieldLabel>
          <textarea
            id="gpsLocation"
            value={data.gpsLocation}
            onChange={(e) => onChange("gpsLocation", e.target.value)}
            disabled={isLoading}
            placeholder="Enter GPS coordinates or location details (optional)"
            maxLength={100}
            aria-invalid={!!fieldErrors.gpsLocation}
            className="flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm aria-invalid:border-destructive aria-invalid:ring-destructive/20"
          />
          <FieldErrorBlock messages={fieldErrors.gpsLocation} />
        </Field>
      </div>

      {/* Business Information */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Business Information</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
          <Field>
            <FieldLabel htmlFor="customerGroupId">Customer Group</FieldLabel>
            <Select
              value={data.customerGroupId}
              onValueChange={(value) => {
                onChange("customerGroupId", value);
                // Auto-populate credit limit and payment terms from selected group
                const selectedGroup = customerGroups?.find((g) => g.id === value);
                if (selectedGroup) {
                  onChange("creditLimit", selectedGroup.defaultCreditLimit);
                  onChange("paymentTerms", selectedGroup.defaultPaymentTerms);
                }
              }}
            >
              <SelectTrigger disabled={isLoading || groupsLoading} aria-invalid={!!fieldErrors.customerGroupId}>
                <SelectValue placeholder="Select Customer Group" />
              </SelectTrigger>
              <SelectContent>
                {customerGroups?.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldErrorBlock messages={fieldErrors.customerGroupId} />
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-700">
                <span className="font-semibold">ℹ️ Note:</span> If a customer group has a discount, the group discount will be applied instead of individual customer discounts.
              </p>
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="creditLimit">Credit Limit</FieldLabel>
            <Input
              id="creditLimit"
              type="number"
              value={data.creditLimit}
              onChange={(e) =>
                onChange("creditLimit", parseFloat(e.target.value) || 0)
              }
              disabled={isLoading}
              aria-invalid={!!fieldErrors.creditLimit}
            />
          </Field>
        </div>

        <Field className="mt-4">
          <FieldLabel htmlFor="paymentTerms">Payment Terms</FieldLabel>
          {termsLoading ? (
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          ) : (
            <Select
              value={data.paymentTerms}
              onValueChange={(value) => onChange("paymentTerms", value)}
            >
              <SelectTrigger disabled={isLoading || termsLoading}>
                <SelectValue placeholder="Select Payment Terms (optional)" />
              </SelectTrigger>
              <SelectContent>
                {paymentTerms?.map((term) => (
                  <SelectItem key={term.value} value={term.value}>
                    {term.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <FieldErrorBlock messages={fieldErrors.paymentTerms} />
        </Field>
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Saving..." : submitButtonText}
      </Button>
    </form>
  );
};
